import type {
  TechnicianListItem,
  TechnicianMetaData,
  TechnicianOrderStatItem,
  UpcomingService,
} from '@/api/technicianslist'
import { DEFAULT_ORDER_STAT_LABELS } from '@/api/technicianslist'

export interface TechnicianOrderDisplayLine {
  label: string
  count?: number
  display: string
  /** 对应 order_stats 字段名，如 hmc_orders / fu_orders / ad_orders */
  statKey?: string
}

export type OrderStatLabelMap = Record<string, string>

function normalizeOrderLine(raw: unknown): TechnicianOrderDisplayLine | null {
  if (raw == null) return null

  if (typeof raw === 'string') {
    const text = raw.trim()
    return text ? { label: '', display: text } : null
  }

  if (typeof raw !== 'object') return null

  const item = raw as TechnicianOrderStatItem
  const display = typeof item.display === 'string' ? item.display.trim() : ''
  const text = typeof item.text === 'string' ? item.text.trim() : ''
  const label = typeof item.label === 'string' ? item.label.trim() : ''
  const count = typeof item.count === 'number' ? item.count : undefined
  const statKey =
    typeof item.key === 'string'
      ? item.key.trim()
      : typeof item.stat_key === 'string'
        ? item.stat_key.trim()
        : undefined

  if (display) return { label, count, display, statKey }
  if (text) return { label, count, display: text, statKey }
  if (label && count != null) return { label, count, display: '', statKey }
  if (label) return { label, display: label, statKey }

  return null
}

function linesFromOrderStatItems(items: unknown[] | undefined): TechnicianOrderDisplayLine[] {
  if (!items?.length) return []
  return items
    .map(normalizeOrderLine)
    .filter((line): line is TechnicianOrderDisplayLine => line != null)
}

const ORDER_STAT_KEYS = ['hmc_orders', 'fu_orders', 'ad_orders', 'hmc', 'fu', 'ad'] as const

function readOrderStatLabel(record: Record<string, unknown>, key: string): string {
  const labelKey = key.endsWith('_orders') ? `${key}_label` : `${key}_orders_label`
  const fromRecord = record[labelKey] ?? record[`${key}_label`]
  return typeof fromRecord === 'string' ? fromRecord.trim() : ''
}

function readOrderStatDisplay(record: Record<string, unknown>, key: string): string {
  const displayKey = key.endsWith('_orders') ? `${key}_display` : `${key}_orders_display`
  const fromRecord = record[displayKey] ?? record[`${key}_display`]
  return typeof fromRecord === 'string' ? fromRecord.trim() : ''
}

function resolveOrderStatLabel(
  key: string,
  record: Record<string, unknown>,
  labelMap: OrderStatLabelMap,
): string {
  const fromList = readOrderStatLabel(record, key)
  if (fromList) return fromList
  const fromMeta = labelMap[key]?.trim() || labelMap[`${key}_label`]?.trim()
  if (fromMeta) return fromMeta
  return DEFAULT_ORDER_STAT_LABELS[key] ?? ''
}

function linesFromOrderStatsObject(
  record: Record<string, unknown>,
  labelMap: OrderStatLabelMap,
): TechnicianOrderDisplayLine[] {
  if (Array.isArray(record.items)) {
    return linesFromOrderStatItems(record.items)
  }

  const lines: TechnicianOrderDisplayLine[] = []

  for (const key of ORDER_STAT_KEYS) {
    const value = record[key]
    if (value == null) continue

    if (typeof value === 'object' && !Array.isArray(value)) {
      const nested = normalizeOrderLine(value)
      if (nested) lines.push({ ...nested, statKey: nested.statKey ?? key })
      continue
    }

    if (typeof value !== 'number' || value <= 0) continue

    const display = readOrderStatDisplay(record, key)
    if (display) {
      lines.push({
        label: readOrderStatLabel(record, key) || resolveOrderStatLabel(key, record, labelMap),
        count: value,
        display,
        statKey: key,
      })
      continue
    }

    const label = resolveOrderStatLabel(key, record, labelMap)
    if (label) {
      lines.push({ label, count: value, display: '', statKey: key })
    }
  }

  return lines
}

/** 订单统计标签字典：仅来自 POST `/api/admin/technician/meta` */
export function buildOrderStatLabelMap(meta: TechnicianMetaData | null | undefined): OrderStatLabelMap {
  const map: OrderStatLabelMap = {}

  const rawLabels = meta?.order_stat_labels
  if (rawLabels && typeof rawLabels === 'object') {
    for (const [key, value] of Object.entries(rawLabels)) {
      if (typeof value === 'string' && value.trim()) {
        map[key] = value.trim()
      }
    }
  }

  const typeSources = [meta?.order_stat_types, meta?.order_types]
  for (const source of typeSources) {
    if (!Array.isArray(source)) continue
    for (const entry of source) {
      if (typeof entry === 'string') {
        map[entry] = entry
        continue
      }
      const value = String(entry.value ?? '').trim()
      const label = String(entry.label ?? '').trim()
      if (value && label) map[value] = label
    }
  }

  return map
}

/** 订单列展示行：数据均来自 list 接口（标签字典可选来自 meta 接口） */
export function resolveTechnicianOrderLines(
  item: Pick<TechnicianListItem, 'order_stats' | 'order_stat_items' | 'no_orders_text'>,
  labelMap: OrderStatLabelMap = {},
): TechnicianOrderDisplayLine[] {
  if (item.order_stat_items?.length) {
    return linesFromOrderStatItems(item.order_stat_items)
  }

  const orderStats = item.order_stats
  if (Array.isArray(orderStats)) {
    return linesFromOrderStatItems(orderStats)
  }

  if (orderStats && typeof orderStats === 'object') {
    const lines = linesFromOrderStatsObject(orderStats as Record<string, unknown>, labelMap)
    if (lines.length > 0) return lines
  }

  const noOrdersText = item.no_orders_text?.trim()
  return noOrdersText ? [{ label: '', display: noOrdersText }] : []
}

export function resolveUpcomingPlanLabel(
  item: Pick<
    TechnicianListItem,
    'upcoming_service' | 'upcoming_none_text' | 'upcoming_plan_label'
  >,
): string {
  const topLevel = item.upcoming_plan_label?.trim()
  if (topLevel) return topLevel

  const upcomingService = item.upcoming_service
  if (typeof upcomingService === 'string' && upcomingService.trim()) {
    return upcomingService.trim()
  }

  if (upcomingService && typeof upcomingService === 'object') {
    const svc = upcomingService
    const labelCandidates = [
      svc.plan_label,
      svc.label,
      svc.name,
      svc.title,
      svc.service_name,
      svc.plan_key,
    ]
    for (const candidate of labelCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }
  }

  return item.upcoming_none_text?.trim() || (upcomingService == null ? 'None' : '')
}

function composeUpcomingDisplayFromParts(service: UpcomingService): string {
  const display = typeof service.display === 'string' ? service.display.trim() : ''
  if (display) return display

  const date = typeof service.date === 'string' ? service.date.trim() : ''
  const timeStart = typeof service.time_start === 'string' ? service.time_start.trim() : ''
  const timeEnd = typeof service.time_end === 'string' ? service.time_end.trim() : ''

  if (date && timeStart && timeEnd) return `${date}, ${timeStart} - ${timeEnd}`
  if (date && timeStart) return `${date}, ${timeStart}`
  if (date) return date
  return ''
}

export function resolveUpcomingDisplay(
  item: Pick<TechnicianListItem, 'upcoming_service' | 'upcoming_display' | 'create_time' | 'update_time'>,
): string {
  const topLevel = item.upcoming_display?.trim()
  if (topLevel) return topLevel

  const upcomingService = item.upcoming_service
  if (upcomingService && typeof upcomingService === 'object') {
    return composeUpcomingDisplayFromParts(upcomingService)
  }

  if (typeof upcomingService === 'string' && upcomingService.trim()) {
    return ''
  }

  const createTime = item.create_time?.trim()
  const updateTime = item.update_time?.trim()
  if (createTime && updateTime) return `${createTime} - ${updateTime}`
  return createTime || updateTime || ''
}

export function resolveWorkRoleText(item: Pick<TechnicianListItem, 'work_role' | 'work_role_text'>): string {
  const raw = item.work_role_text?.trim() || item.work_role?.trim() || ''
  if (!raw) return ''
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

export function resolveUpcomingService(
  upcomingService: TechnicianListItem['upcoming_service'],
): UpcomingService | string | null {
  return upcomingService ?? null
}

/** 仅保留数字 */
export function normalizePhoneDigits(value: string): string {
  return (value ?? '').replace(/[^\d]/g, '')
}

/** 北美号码：去掉前导国家码 1，得到最多 10 位本地号 */
export function normalizePhoneSearchDigits(value: string): string {
  let digits = normalizePhoneDigits(value)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  return digits
}

/** 10 位本地号格式化为 437-326-8588（与列表展示/后端检索一致） */
export function formatNorthAmericanPhoneLocal(digits: string): string | null {
  const local = normalizePhoneSearchDigits(digits)
  if (local.length !== 10) return null
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`
}

/** 提交给后端的手机号关键词：纯数字 / +1 等统一成 XXX-XXX-XXXX */
export function normalizeMobileSearchKeyword(keyword: string): string {
  const trimmed = keyword.trim()
  if (!trimmed) return trimmed
  return formatNorthAmericanPhoneLocal(trimmed) ?? trimmed
}

function mobileMatchesKeyword(
  item: Pick<TechnicianListItem, 'mobile' | 'phone'>,
  keyword: string,
): boolean {
  const key = keyword.trim()
  if (!key) return true

  const stored = (item.mobile || item.phone || '').trim()
  if (!stored) return false

  const storedLower = stored.toLowerCase()
  const keyLower = key.toLowerCase()
  if (storedLower.includes(keyLower)) return true

  const searchDigits = normalizePhoneSearchDigits(key)
  if (!searchDigits) return false

  const storedDigits = normalizePhoneDigits(stored)
  const storedLocal = normalizePhoneSearchDigits(stored)

  if (storedLocal.includes(searchDigits) || storedDigits.includes(searchDigits)) {
    return true
  }

  const formattedSearch = formatNorthAmericanPhoneLocal(searchDigits)
  if (formattedSearch && storedLower.includes(formattedSearch.toLowerCase())) {
    return true
  }

  return false
}

/** 后端 search_type 对部分字段匹配不可靠时，按 UI 所选类型在本地二次校验 */
export function matchesTechnicianListSearch(
  item: Pick<TechnicianListItem, 'technician_no' | 'mobile' | 'phone' | 'name'>,
  searchType: string,
  keyword: string,
): boolean {
  const key = keyword.trim().toLowerCase()
  if (!key) return true

  switch (searchType) {
    case 'mobile_no':
      return mobileMatchesKeyword(item, keyword)
    case 'technician_id':
      return (item.technician_no || '').toLowerCase().includes(key)
    case 'name':
      return (item.name || '').toLowerCase().includes(key)
    default:
      return true
  }
}

export function refineTechnicianListSearchResults(
  list: TechnicianListItem[],
  searchType: string,
  keyword: string,
): TechnicianListItem[] {
  const key = keyword.trim()
  if (!key) return list
  return list.filter((item) => matchesTechnicianListSearch(item, searchType, key))
}
